import React, { useMemo, useRef, useState } from "react";
import { useEntity } from "@/hooks/useEntity";
import { citationEntityConfig } from "@/entities/Citation";
import {
  formatCitation,
  buildInlineReferenceVariants,
  composeCombinedInlineReference,
  type InlineVariantKey,
} from "@/lib/paperFormats";
import type { PaperFormat } from "@/lib/paperFormats";
import { extractTextFromFile } from "@/lib/extractDocumentContent";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BookOpen, Plus, Pencil, Trash2, X, Upload, Loader2, Link as LinkIcon, Rows3 } from "lucide-react";

type CitationRecord = {
  id: number;
  documentId: number;
  citationType: string;
  authors: string;
  title: string;
  year: string;
  publisher?: string;
  url?: string;
  doi?: string;
  pages?: string;
  volume?: string;
  issue?: string;
};

type ReferenceListPanelProps = {
  documentId: number;
  paperFormat: PaperFormat;
  onInsertCitation: (citationText: string) => void;
  referencesOverride?: CitationRecord[];
  onReferencesChange?: (next: CitationRecord[]) => void;
  onInsertLinkedReference?: (ref: CitationRecord, variant: InlineVariantKey, text: string) => void;
  onReferenceUpdated?: (ref: CitationRecord) => void;
  onReferenceDeleted?: (id: number) => void;
};

const emptyForm = {
  citationType: "book",
  authors: "",
  title: "",
  year: "",
  publisher: "",
  url: "",
  doi: "",
  pages: "",
  volume: "",
  issue: "",
};

const ReferenceListPanel: React.FC<ReferenceListPanelProps> = ({
  documentId,
  paperFormat,
  onInsertCitation,
  referencesOverride,
  onReferencesChange,
  onInsertLinkedReference,
  onReferenceUpdated,
  onReferenceDeleted,
}) => {
  const { items, create, update, delete: deleteRef, reload } = useEntity(citationEntityConfig);
  const usingControlled = Boolean(referencesOverride && onReferencesChange);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [editingId, setEditingId] = useState<number | null>(null);
  const [selectedRefs, setSelectedRefs] = useState<number[]>([]);
  const [importing, setImporting] = useState(false);
  const [importFeedback, setImportFeedback] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Filter and sort references for this document
  const references = useMemo(() => {
    const sourceItems = usingControlled ? (referencesOverride as CitationRecord[]) : items;
    return sourceItems
      .filter((ref) => ref.documentId === documentId)
      .sort((a, b) => (a.title || "").localeCompare(b.title || ""));
  }, [documentId, items, referencesOverride, usingControlled]);

  const persistControlled = (updatedDocRefs: CitationRecord[]) => {
    if (!onReferencesChange || !referencesOverride) return;
    const others = referencesOverride.filter((ref) => ref.documentId !== documentId);
    onReferencesChange([...others, ...updatedDocRefs]);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleAddOrUpdate = async () => {
    if (!form.title || !form.authors || !form.year) return;
    if (usingControlled) {
      const nextId = editingId ?? Date.now();
      const updatedDocRefs = references.filter((ref) => ref.id !== nextId);
      updatedDocRefs.push({
        id: nextId,
        documentId,
        ...form,
        citationType: form.citationType || "book",
      });
      persistControlled(updatedDocRefs);
      if (editingId) {
        const updated = updatedDocRefs.find((ref) => ref.id === nextId);
        if (updated) onReferenceUpdated?.(updated);
      }
    } else {
      if (editingId) {
        await update(editingId, {
          ...form,
          documentId,
          citationType: form.citationType || "book",
        });
        const updated = references.find((ref) => ref.id === editingId);
        if (updated) {
          onReferenceUpdated?.({ ...updated, ...form });
        }
      } else {
        await create({
          ...form,
          documentId,
          citationType: form.citationType || "book",
        });
      }
    }
    setForm({ ...emptyForm });
    setShowForm(false);
    setEditingId(null);
    if (!usingControlled) {
      reload();
    }
  };

  const handleEdit = (ref: any) => {
    setForm({
      citationType: ref.citationType || "book",
      authors: ref.authors || "",
      title: ref.title || "",
      year: ref.year || "",
      publisher: ref.publisher || "",
      url: ref.url || "",
      doi: ref.doi || "",
      pages: ref.pages || "",
      volume: ref.volume || "",
      issue: ref.issue || "",
    });
    setEditingId(ref.id);
    setShowForm(true);
  };

  const handleCancelEdit = () => {
    setForm({ ...emptyForm });
    setEditingId(null);
    setShowForm(false);
  };

  const handleDelete = async (id: number) => {
    if (usingControlled) {
      const updatedDocRefs = references.filter((ref) => ref.id !== id);
      persistControlled(updatedDocRefs);
    } else {
      await deleteRef(id);
      reload();
    }
    setSelectedRefs((prev) => prev.filter((refId) => refId !== id));
    onReferenceDeleted?.(id);
  };

  const handleToggleSelect = (id: number) => {
    setSelectedRefs((prev) =>
      prev.includes(id) ? prev.filter((refId) => refId !== id) : [...prev, id]
    );
  };

  const handleInsertVariant = (ref: CitationRecord, variant: InlineVariantKey) => {
    const variants = buildInlineReferenceVariants(ref, paperFormat);
    const text = variants[variant];
    if (!text) return;
    if (onInsertLinkedReference) {
      onInsertLinkedReference(ref, variant, text);
    } else {
      onInsertCitation(text);
    }
  };

  const handleInsertCombined = () => {
    const refs = selectedRefs
      .map((id) => references.find((ref) => ref.id === id))
      .filter((ref): ref is CitationRecord => Boolean(ref));
    if (!refs.length) return;
    const combined = composeCombinedInlineReference(refs, paperFormat);
    onInsertCitation(combined);
    setSelectedRefs([]);
  };

  const handleFileButton = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    await importReferencesFromFile(file);
    event.target.value = "";
  };

  const importReferencesFromFile = async (file: File) => {
    setImporting(true);
    setImportFeedback(null);
    try {
      const text = await extractTextFromFile(file);
      const parsed = parseReferencesFromText(text);
      if (!parsed.length) {
        setImportFeedback("No reference-style lines detected in that file.");
        setImporting(false);
        return;
      }

      if (usingControlled) {
        const updatedDocRefs = [...references];
        parsed.forEach((entry, idx) => {
          updatedDocRefs.push({
            id: Date.now() + idx,
            documentId,
            citationType: entry.citationType || "article",
            authors: entry.authors || "Imported Author",
            title: entry.title || `Imported reference ${idx + 1}`,
            year: entry.year || "n.d.",
            publisher: entry.publisher || "",
            url: entry.url || "",
            doi: entry.doi || "",
            pages: entry.pages || "",
            volume: entry.volume || "",
            issue: entry.issue || "",
          });
        });
        persistControlled(updatedDocRefs);
      } else {
        for (const entry of parsed) {
          await create({
            documentId,
            citationType: entry.citationType || "article",
            authors: entry.authors || "Imported Author",
            title: entry.title || "Imported reference",
            year: entry.year || "n.d.",
            publisher: entry.publisher || "",
            url: entry.url || "",
            doi: entry.doi || "",
            pages: entry.pages || "",
            volume: entry.volume || "",
            issue: entry.issue || "",
          });
        }
        reload();
      }
      setImportFeedback(`Imported ${parsed.length} reference${parsed.length === 1 ? "" : "s"}.`);
    } catch (err) {
      console.error("Failed to import references", err);
      setImportFeedback("Could not read that file. Please try another DOCX or PDF.");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-purple-300" />
          <div>
            <p className="font-semibold text-white">Reference Management</p>
            <p className="text-[11px] text-white/60">Keep references synced with in-text citations.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={handleFileButton} disabled={importing}>
            {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            Import DOCX/PDF
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setShowForm((v) => !v);
              setEditingId(null);
              setForm({ ...emptyForm });
            }}
          >
            <Plus className="w-4 h-4 mr-1" /> Add
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.doc,.docx"
            className="hidden"
            onChange={handleFileSelected}
          />
        </div>
      </div>
      {importFeedback && (
        <div className="text-[11px] text-white/60 bg-white/5 border border-white/10 rounded-lg px-3 py-2">
          {importFeedback}
        </div>
      )}
      {showForm && (
        <div className="mb-4 space-y-2 bg-white/5 p-3 rounded-lg">
          <div className="flex justify-between items-center mb-1">
            <span className="font-semibold text-white">{editingId ? "Edit Reference" : "Add Reference"}</span>
            <Button size="icon" variant="ghost" onClick={handleCancelEdit}>
              <X className="w-4 h-4" />
            </Button>
          </div>
          <select
            name="citationType"
            value={form.citationType}
            onChange={handleChange}
            className="w-full px-2 py-1 rounded bg-white/10 text-white"
          >
            <option value="book">Book</option>
            <option value="journal">Journal Article</option>
            <option value="website">Website</option>
            <option value="article">Article</option>
            <option value="other">Other</option>
          </select>
          <Input name="authors" value={form.authors} onChange={handleChange} placeholder="Authors" />
          <Input name="title" value={form.title} onChange={handleChange} placeholder="Title" />
          <Input name="year" value={form.year} onChange={handleChange} placeholder="Year" />
          <Input name="publisher" value={form.publisher} onChange={handleChange} placeholder="Publisher" />
          <Input name="url" value={form.url} onChange={handleChange} placeholder="URL" />
          <Input name="doi" value={form.doi} onChange={handleChange} placeholder="DOI" />
          <Input name="pages" value={form.pages} onChange={handleChange} placeholder="Pages" />
          <Input name="volume" value={form.volume} onChange={handleChange} placeholder="Volume" />
          <Input name="issue" value={form.issue} onChange={handleChange} placeholder="Issue" />
          <Button size="sm" className="w-full mt-2" onClick={handleAddOrUpdate}>
            {editingId ? "Update Reference" : "Save Reference"}
          </Button>
        </div>
      )}
      {selectedRefs.length >= 2 && (
        <div className="bg-white/5 border border-white/15 rounded-lg p-3 flex flex-wrap items-center gap-3">
          <div className="text-xs text-white/70 flex items-center gap-1">
            <Rows3 className="w-4 h-4 text-purple-300" />
            {selectedRefs.length} references selected
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={handleInsertCombined}>
              Insert combined citation
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSelectedRefs([])}>
              Clear
            </Button>
          </div>
        </div>
      )}
      {references.length === 0 ? (
        <div className="text-slate-400 text-center py-8">
          No references yet.
        </div>
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-2 snap-x">
          {references.map((ref) => {
            const variants = buildInlineReferenceVariants(ref, paperFormat);
            const isSelected = selectedRefs.includes(ref.id);
            return (
              <div
                key={ref.id}
                className={`min-w-[320px] snap-start shrink-0 bg-white/5 border rounded-xl p-3 space-y-3 ${
                  isSelected ? "border-purple-400/50" : "border-white/10"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-white flex items-center gap-2">
                      {ref.title || "Untitled source"}
                      <button
                        className={`text-[10px] uppercase tracking-[0.2em] rounded-full px-2 py-0.5 border ${
                          isSelected ? "border-purple-400 text-purple-200" : "border-white/20 text-white/50"
                        }`}
                        onClick={() => handleToggleSelect(ref.id)}
                      >
                        {isSelected ? "Selected" : "Select"}
                      </button>
                    </div>
                    <p className="text-xs text-white/70 mt-1">
                      {formatCitation(ref, paperFormat)}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" onClick={() => handleEdit(ref)}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => handleDelete(ref.id)}>
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <Button
                    size="sm"
                    variant="outline"
                    className="justify-center"
                    onClick={() => handleInsertVariant(ref, "narrative")}
                  >
                    {variants.narrative}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="justify-center"
                    onClick={() => handleInsertVariant(ref, "parenthetical")}
                  >
                    {variants.parenthetical}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="justify-center"
                    onClick={() => handleInsertVariant(ref, "bare")}
                  >
                    {variants.bare}
                  </Button>
                </div>
                <div className="pt-2 border-t border-white/10 flex flex-col gap-2">
                  <Button
                    size="sm"
                    className="w-full"
                    onClick={() => onInsertCitation(formatCitation(ref, paperFormat))}
                  >
                    Insert full reference
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-white/70 flex items-center justify-center gap-2"
                    onClick={() => handleToggleSelect(ref.id)}
                  >
                    <LinkIcon className="w-4 h-4" /> Add to combined citation
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const parseReferencesFromText = (raw: string) => {
  return raw
    .split(/\n+/)
    .map((line) => line.trim())
    .filter((line) => line.length > 25 && /(19|20)\d{2}/.test(line))
    .slice(0, 12)
    .map((line, idx) => {
      const yearMatch = line.match(/\((19|20)\d{2}\)/) || line.match(/(19|20)\d{2}/);
      const year = yearMatch ? yearMatch[0].replace(/[()]/g, "") : "n.d.";
      const before = yearMatch && yearMatch.index !== undefined ? line.slice(0, yearMatch.index).trim() : line;
      const after = yearMatch && yearMatch.index !== undefined
        ? line.slice(yearMatch.index + yearMatch[0].length).trim()
        : "";
      return {
        citationType: "article" as const,
        authors: before.replace(/[\.:;]$/, "") || `Imported Author ${idx + 1}`,
        title: after.replace(/^[\-–:,\s]+/, "") || line,
        year,
      };
    });
};

export default ReferenceListPanel;